import type { ComponentProps } from "react";
import type { BillOut } from "../../../../api/bills";
import { TableRailSkeleton } from "../OrderingSkeleton";
import { OrderDetails } from "./OrderDetails";
import { TableFooter } from "./TableFooter";
import { TableList } from "./TableList";
import { TableRail } from "./TableRail";
import { OrderingSchemaView } from "./schema/OrderingSchemaView";
import { SchemaTablePanel } from "./schema/SchemaTablePanel";
import { schemaTableOf } from "./schema/orderingFloorData";
import type { OrderingFloor } from "./schema/useOrderingFloor";
import type { SchemaSelection } from "./schema/useSchemaSelection";

type DetailsProps = Omit<ComponentProps<typeof OrderDetails>, "bill" | "layout" | "onShowTables">;

interface TablesScreenProps {
  /** Режим „Схема" (само на десктоп); null — списъкът. */
  schema: {
    floor: OrderingFloor;
    selection: SchemaSelection;
    /** Двоен клик на временна маса/карта — „Добави" (без право — undefined). */
    onOpenBill?: (billId: number) => void;
  } | null;
  selectedBill: BillOut | null;
  closingBillIds: ReadonlySet<number>;
  list: ComponentProps<typeof TableList>;
  /** Сметките още се зареждат (първи път) — скелетон в релсата. */
  listLoading: boolean;
  /** Скрита релса с маси (тесен екран, след двоен клик). */
  railHidden: boolean;
  onShowRail?: () => void;
  details: DetailsProps;
  footer: ComponentProps<typeof TableFooter>;
}

/**
 * Екранът с масите на „Маркиране": списъкът (релсата вляво + поръчаното
 * вдясно) или схемата (залата вляво + панелът на масата вдясно) и под тях
 * ЕДНА лента с бутоните — на едно и също място в двата режима, за да не губи
 * състоянието си (чакащата смяна на плащане, печатът в ход) при превключване.
 */
export const TablesScreen = ({
  schema,
  selectedBill,
  closingBillIds,
  list,
  listLoading,
  railHidden,
  onShowRail,
  details,
  footer,
}: TablesScreenProps) => {
  const selectedTable = schema?.selection.selectedTable ?? null;
  const selectedTableName = selectedTable?.table.name ?? "";
  // Местата: на избраната маса, иначе на масата от схемата с името на
  // сметката (нефискализираната не е на маса, но името ѝ е от схемата).
  const seatsTable =
    selectedTable ?? (schema && selectedBill ? schemaTableOf(selectedBill, schema.floor.index) : null);
  const hiddenOpenTable = selectedTable
    ? (schema?.floor.hiddenOpenTableOf({ id: selectedTable.table.id, name: selectedTableName }) ?? null)
    : null;

  return (
    <>
      {/* Резервираното място за фиксирания футър е в цвета на панела
          (иначе прозира ивица pos-canvas) и съответства на реалната му
          височина: тесен екран 63px (pt-2 + 46px бутони + pb-8 + border),
          от sm нагоре 77px (py-3 + 52px бутони + border) */}
      <div className="flex flex-1 overflow-hidden pb-[calc(63px+env(safe-area-inset-bottom,0px)*0.5)] sm:pb-[calc(77px+env(safe-area-inset-bottom,0px)*0.5)] min-h-0 bg-pos-panel">
        {schema ? (
          <>
            <OrderingSchemaView
              floor={schema.floor}
              selection={schema.selection}
              closingBillIds={closingBillIds}
              closingTableKeys={schema.floor.closingTableKeys}
              selectedBillId={selectedBill?.id ?? null}
              onOpenBill={schema.onOpenBill}
            />
            <SchemaTablePanel
              table={selectedTable}
              tableState={
                selectedTable
                  ? schema.floor.snapshot.stateOf({
                      id: selectedTable.table.id,
                      name: selectedTableName,
                    })
                  : null
              }
              takenBy={hiddenOpenTable ? { waiterName: hiddenOpenTable.waiterName } : null}
              seats={seatsTable ? (seatsTable.table.seats ?? 0) : null}
              bills={schema.selection.selectedTableBills}
              selectedBill={selectedBill}
              selectedBillTableNames={selectedBill ? schema.floor.tableNamesOfBill(selectedBill) : []}
              onSelectBill={schema.selection.selectBill}
              details={details}
            />
          </>
        ) : (
          <>
            {/* Sidebar с маси - вляво (на тесен екран може да е скрит с двоен
                клик); под списъка на десктоп е часовникът */}
            <TableRail hidden={railHidden}>
              {listLoading ? <TableRailSkeleton /> : <TableList {...list} />}
            </TableRail>

            {/* Main content - детайли на поръчките - вдясно */}
            <main className="flex-1 overflow-hidden bg-pos-canvas">
              <OrderDetails bill={selectedBill} onShowTables={onShowRail} {...details} />
            </main>
          </>
        )}
      </div>

      {/* Footer за маси */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <TableFooter {...footer} />
      </div>
    </>
  );
};
